import { Button } from '@progress/kendo-react-buttons';
import { LayoutContext } from 'app/components/ReactContexts/layoutContext';
import { AccessSharedWith } from 'app/components/access-sharing';
import {
  MachineOwnerCard,
  SharedUserAccess,
} from 'app/components/access-sharing/cards';
import { ChangeUserPermissionsDialog } from 'app/components/access-sharing/dialogs';
import { CounterBadge } from 'app/components/badges';
import { DeleteDialog } from 'app/components/dialogs';
import { AddIcon } from 'app/components/icons';
import { BackActionSplitCardContentLayout } from 'app/components/layouts';
import { ActionsListItem, EllipsisMenu } from 'app/components/menu';
import useHasAccessSharingPermissions from 'app/hooks/useHasAccessSharingPermissions';
import { getDefaultThumbnail } from 'app/pages/Equipment/utils';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import './MachineDetailsPage.less';
import { formatDate } from 'utils/date';
import useGetEquipmentData from 'app/hooks/useGetEquipmentData';
import { ErrorNotification, Success } from 'app/components/Notifications';
import { useNotificationsContext } from 'context';
import { SharedItemShareType } from 'services/api/shares';
import { invitationsApi } from 'services/api/invitations/queries';

export default function MachineDetailsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { setClassNames, removeClassNames } = React.useContext(LayoutContext);

  const { id } = useParams();
  const [revokeInvitation] = invitationsApi.useRevokeInvitationMutation();
  const { addNotification, addSnackbar } = useNotificationsContext();
  const { selectedEquipment: equipment } = useGetEquipmentData(id!);

  // More appropriate entity name will be set when API is ready.
  const [selectedUserAccessToChange, setSelectedUserAccessToChange] = useState<
    SharedUserAccess | undefined
  >();
  const isChangeUserPermissionsDialogOpen = !!selectedUserAccessToChange;

  const [isRevokeAllUserAccessDialogOpen, setIsRevokeAllUserAccessDialogOpen] =
    useState(false);

  const [selectedUserAccessToRevoke, setSelectedUserAccessToRevoke] = useState<
    SharedUserAccess | undefined
  >();

  const isRevokeUserAccessDialogOpen = !!selectedUserAccessToRevoke;

  const hasAccess = useHasAccessSharingPermissions(equipment?.role);

  const userAccess = useMemo(() => {
    const activeUserAccess = equipment?.shareData?.sharedWith
      .filter(
        sharedWith =>
          !sharedWith.endsAt || new Date(sharedWith.endsAt) > new Date(),
      )
      .map(sharedWith => ({
        id: sharedWith.exposedAccessSharingRecordId,
        firstName: sharedWith.displayName,
        lastName: '',
        email: sharedWith.email,
        role: sharedWith.sharedRole,
        startTime: sharedWith.startsAt
          ? formatDate(sharedWith.startsAt, 'DATE_FORMAT_DATETIME')
          : undefined,
        endTime: sharedWith.endsAt
          ? formatDate(sharedWith.endsAt, 'DATE_FORMAT_DATETIME')
          : undefined,
      }));

    const expiredUserAccess = equipment?.shareData?.sharedWith
      .filter(
        sharedWith =>
          sharedWith.endsAt && new Date(sharedWith.endsAt) < new Date(),
      )
      .map(sharedWith => ({
        id: sharedWith.exposedAccessSharingRecordId,
        firstName: sharedWith.displayName,
        lastName: '',
        email: sharedWith.email,
        role: sharedWith.sharedRole,
        startTime: sharedWith.startsAt
          ? formatDate(sharedWith.startsAt, 'DATE_FORMAT_DATETIME')
          : undefined,
        endTime: sharedWith.endsAt
          ? formatDate(sharedWith.endsAt, 'DATE_FORMAT_DATETIME')
          : undefined,
      }));

    return {
      activeUserAccess,
      expiredUserAccess,
    };
  }, [equipment?.shareData?.sharedWith]);

  const mainMenuActions = useMemo<ActionsListItem[]>(() => {
    return [
      {
        label: t('access_sharing.role_based_actions.remove_all_users'),
        action: () => setIsRevokeAllUserAccessDialogOpen(true),
      },
    ];
  }, [hasAccess, t, setIsRevokeAllUserAccessDialogOpen]);

  React.useEffect(() => {
    const classNameIndex = setClassNames('layout-grey');
    return () => {
      removeClassNames(classNameIndex);
    };
  }, []);

  const handleRevokeAllAccess = async () => {
    const exposedAccessSharingRecordIds = equipment?.shareData?.sharedWith.map(
      sharedWith => sharedWith.exposedAccessSharingRecordId,
    );

    if (!exposedAccessSharingRecordIds) return;

    try {
      await revokeInvitation(
        exposedAccessSharingRecordIds.map(id => ({
          shareType: SharedItemShareType.EQUIPMENT,
          exposedAccessSharingRecordId: id,
        })),
      );
      addSnackbar(
        <div className="snackbar-container">
          <span>
            {t('access_sharing.snackbars.you_removed_all_users_access')}
          </span>
        </div>,
      );
    } catch (error) {
      console.error(error);
      addNotification(<ErrorNotification text={'Server error'} />);
    } finally {
      setIsRevokeAllUserAccessDialogOpen(false);
    }
  };

  const handleRevokeAccess = async () => {
    try {
      if (!equipment?.isSharedIndividually) return;
      const exposedAccessSharingRecordId =
        equipment?.shareData?.sharedWith.find(
          sharedWith => sharedWith.email === selectedUserAccessToRevoke?.email,
        )?.exposedAccessSharingRecordId;
      await revokeInvitation([
        {
          shareType: 'equipment',
          exposedAccessSharingRecordId: exposedAccessSharingRecordId!,
        },
      ]);
      addNotification(
        <Success text={t('access_sharing.snackbars.access-revoked')} />,
      );
    } catch (error) {
      console.error(error);
      addNotification(<ErrorNotification text={'Server error'} />);
    } finally {
      setSelectedUserAccessToRevoke(undefined);
    }
  };

  useEffect(() => {
    if (!equipment?.isSharedIndividually) {
      handleBack();
    }
  }, [equipment?.isSharedIndividually]);

  function handleBack() {
    navigate('/access/equipment-and-fleets');
  }

  // TODO Request a design
  if (!equipment) {
    return <h1>Equipment Not Found</h1>;
  }

  return (
    <>
      <BackActionSplitCardContentLayout
        backActionProps={{ onBack: handleBack }}
        renderHeader={() => (
          <div className="machine-details-page__header">
            <div className="machine-details-page__info-and-picture-wrapper">
              <div className="machine-details-page__info-wrapper">
                <h1 className="machine-details-page__nickname ellipsis-2-lines">
                  {equipment?.nickName || equipment?.model}
                </h1>

                <div className="machine-details-page__model">
                  <span className="machine-details-page__model-label">
                    {t('equipment_model')}
                  </span>
                  {equipment.model}
                  <span className="machine-details-page__model-label">
                    {t('equipment_serial')}
                  </span>
                  {equipment.pinOrSerial}
                </div>
              </div>

              <div className="machine-details-page__picture">
                <img
                  src={
                    equipment?.modelIconUrl ||
                    getDefaultThumbnail(equipment?.type, equipment?.subcategory)
                  }
                  onError={({ currentTarget }) => {
                    currentTarget.onerror = null;
                    currentTarget.src = getDefaultThumbnail(
                      equipment?.type,
                      equipment?.subcategory,
                    );
                  }}
                  alt="Machine"
                />
              </div>
            </div>

            <MachineOwnerCard
              email={equipment.shareData?.sharedItemDetails.ownerEmail}
              firstName={equipment.shareData?.sharedItemDetails.ownerName}
              lastName={''}
              className="machine-details-page__owner-card"
            />
          </div>
        )}
        renderContent={() => (
          <div className="machine-details-page__content">
            <div className="machine-details-page__user-actions-wrapper">
              <CounterBadge
                badgeContent={
                  (userAccess.activeUserAccess?.length ?? 0) +
                  (userAccess.expiredUserAccess?.length ?? 0)
                }
                text={t('shared_with')}
              />

              <div className="machine-details-page__user-actions">
                {hasAccess('addNewUser') && (
                  <Button
                    className="machine-details-page__add-new-user-btn"
                    themeColor="secondary"
                    onClick={() =>
                      navigate(
                        `/access/shared-equipment/${equipment.id}/add-new-user`,
                      )
                    }
                  >
                    <AddIcon strokeWidth={2} />
                    {t('add_user')}
                  </Button>
                )}

                {hasAccess('revokeAllUsersAccess') && (
                  <EllipsisMenu actions={mainMenuActions} />
                )}
              </div>
            </div>

            <AccessSharedWith
              role={equipment.role!}
              userAccess={userAccess.activeUserAccess ?? []}
              expiredUserAccess={userAccess.expiredUserAccess ?? []}
              onChangeAccess={setSelectedUserAccessToChange}
              onRevokeAccess={setSelectedUserAccessToRevoke}
            />
          </div>
        )}
      />

      {isChangeUserPermissionsDialogOpen && (
        <ChangeUserPermissionsDialog
          equipment={equipment}
          userAccess={selectedUserAccessToChange}
          onClose={() => setSelectedUserAccessToChange(undefined)}
        />
      )}

      <DeleteDialog
        open={isRevokeUserAccessDialogOpen}
        title={t('revoke_access_confirmation', {
          email: selectedUserAccessToRevoke?.email,
          machineName: equipment.nickName || equipment.model,
        })}
        confirmationLabel={t('access_sharing.role_based_actions.revoke')}
        onClose={() => setSelectedUserAccessToRevoke(undefined)}
        onConfirm={handleRevokeAccess}
      />

      <DeleteDialog
        open={isRevokeAllUserAccessDialogOpen}
        title={t('revoke_access_confirmation_all', {
          machineName: equipment.nickName || equipment.model,
        })}
        confirmationLabel={t('access_sharing.role_based_actions.revoke')}
        onClose={() => setIsRevokeAllUserAccessDialogOpen(false)}
        onConfirm={handleRevokeAllAccess}
      />
    </>
  );
}
